/*
 * Keyboard input — WASD / arrows for movement, space for the nibble tap.
 *
 * Keys are accumulated in a map and queried by the game loop each frame.
 * Direction state is read-only outside this module; movement application
 * lives in the loop (because it needs the dt and the lock-out during fishing).
 */

const pressed = new Map<string, boolean>();

let nibbleHandler: (() => void) | null = null;
let firstInputHandler: (() => void) | null = null;
let firstInputFired = false;

export function initKeys(): void {
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
}

function onKeyDown(e: KeyboardEvent): void {
  pressed.set(e.key.toLowerCase(), true);
  if (!firstInputFired && firstInputHandler) {
    firstInputFired = true;
    firstInputHandler();
  }
  if (e.key === ' ' && nibbleHandler) {
    e.preventDefault();
    nibbleHandler();
  }
}

function onKeyUp(e: KeyboardEvent): void {
  pressed.set(e.key.toLowerCase(), false);
}

/** True if any of the listed keys are currently held. */
export function anyKey(...names: string[]): boolean {
  return names.some(n => pressed.get(n) === true);
}

export interface MovementVector {
  dx: number;
  dy: number;
}

/** Per-frame movement vector from WASD / arrow keys (each ±1, not normalized). */
export function readMovement(): MovementVector {
  let dx = 0;
  let dy = 0;
  if (anyKey('a', 'arrowleft')) dx -= 1;
  if (anyKey('d', 'arrowright')) dx += 1;
  if (anyKey('w', 'arrowup')) dy -= 1;
  if (anyKey('s', 'arrowdown')) dy += 1;
  return { dx, dy };
}

/** Register a handler called once on the very first key press (for hiding the hint). */
export function onFirstInput(fn: () => void): void {
  if (firstInputFired) {
    fn();
    return;
  }
  firstInputHandler = fn;
}

/** Mark first-input as having occurred without registering a key handler. */
export function fireFirstInput(): void {
  if (firstInputFired) return;
  firstInputFired = true;
  if (firstInputHandler) firstInputHandler();
}

/** Bind the space-bar nibble tap to a callback. */
export function setNibbleHandler(fn: (() => void) | null): void {
  nibbleHandler = fn;
}
